import * as React from 'react';
import { useEffect } from 'react';
import GoogleButton from 'react-google-button';
import { useRouter } from 'next/router';
import Link from 'components/common/Link';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { sendApiRequest, getUrlApp } from 'lib/api/public';
import LogoutRequired from '../components/LogoutRequired';
import Head from 'next/head';
import Tabs, { TabPanel } from 'components/common/Tabs';
import { PasswordField } from 'components/common/PasswordField';
import { getSubdomain } from 'components/utils';

const API_URL = `${process.env.NEXT_PUBLIC_API_URL || ''}`;
const isTest = process.env.NODE_ENV !== 'production';

export type AuthAlertState = {
  severity: 'info' | 'error' | 'success';
  message: React.ReactNode;
};

export const authAlertSx = { mb: { xs: 2, sm: 3 } } as const;

export const authDividerSx = { width: '100%', mt: { xs: 3, sm: 5 }, mb: 2 } as const;

const authMobileFieldSx = {
  '& .MuiInputBase-input, & .MuiSelect-select': {
    fontSize: { xs: '16px', sm: '0.8125rem' },
  },
} as const;

export function AuthAlert({ alert }: { alert: AuthAlertState | null }) {
  if (!alert) {
    return null;
  }

  return (
    <Alert severity={alert.severity} sx={authAlertSx}>
      {alert.message}
    </Alert>
  );
}

export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return String(error);
}

export const AuthContainer = ({ children, tab = 'login' }: React.PropsWithChildren<{ tab?: string }>) => {
  const router = useRouter();

  let selectedTab = 0;

  if (tab === 'login') {
    selectedTab = 0;
  } else if (tab === 'signup') {
    selectedTab = 1;
  } else if (tab === 'forgot-password') {
    selectedTab = 1;
  }

  const [tabValue] = React.useState(selectedTab);

  const handleTabChange = (newValue: number) => {
    if (newValue === 0) {
      void router.push('/login');
    } else {
      void router.push('/signup');
    }
  };

  const tabs = [
    {
      label: 'Login',
      value: 0,
    },
  ];

  if (tab === 'signup' || tab === 'login') {
    tabs.push({
      label: 'Signup',
      value: 1,
    });
  }

  if (tab === 'forgot-password') {
    tabs.push({
      label: 'Forgot Password',
      value: 1,
    });
  }

  return (
    <Container
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        minHeight: { xs: '100dvh', sm: 'auto' },
        maxWidth: '600px !important',
        px: { xs: 2, sm: 3 },
        pt: { xs: 'max(16px, env(safe-area-inset-top))', sm: 4, md: 6 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 4, md: 6 },
        width: { xs: '100%', sm: '70%', md: '50%', lg: '40%', xl: '28%' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 4 } }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Harbor"
          sx={{ width: '100%', maxWidth: { xs: 160, sm: 200 }, height: 'auto' }}
        />
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 2,
          width: '100%',
          overflowX: 'hidden',
          ...authMobileFieldSx,
        }}
      >
        <Tabs variant="fullWidth" onTabChange={handleTabChange} initialTabValue={tabValue} tabs={tabs} />

        <TabPanel value={tabValue} index={tabValue}>
          {children}
        </TabPanel>
      </Paper>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, px: { xs: 0.5, sm: 0 }, textAlign: 'center', lineHeight: 1.5 }}
      >
        By using Harbor, you agree to the{' '}
        <Link href="https://findharbor.com/terms" target="_blank" rel="noopener noreferrer">
          Terms of Use
        </Link>{' '}
        and{' '}
        <Link href="https://findharbor.com/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </Link>
        .
      </Typography>
    </Container>
  );
};

export const AuthGoogleButton = ({ label }: { label: string }) => {
  const subdomain = getSubdomain();

  return (
    <div className="google-button">
      <GoogleButton
        style={{
          width: '100%',
          marginTop: 16,
          borderRadius: '4px',
          fontFamily: 'Inter, sans-serif',
          minHeight: '48px',
          height: '48px',
          lineHeight: '48px',
          fontSize: '16px',
        }}
        onClick={() => {
          const redirect = new URLSearchParams(window.location.search).get('redirect');
          const redirectUrl = redirect ? `${window.location.origin}${redirect}` : window.location.href.replace('/login', '');

          window.location.href = `${API_URL}/auth/google?redirect_url=${encodeURIComponent(redirectUrl)}&subdomain=${subdomain}`;
        }}
        label={label}
      />
    </div>
  );
};

export default function SignInForm() {
  const router = useRouter();
  const [loading, setLoading] = React.useState(false);
  const [loading2fa, setLoading2fa] = React.useState(false);
  const [require2fa, setRequire2fa] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [twoFactorMethod, setTwoFactorMethod] = React.useState<'otplib' | 'sms'>('otplib');
  const [twoFactorPhone, setTwoFactorPhone] = React.useState<string>('');
  const [sendingSms, setSendingSms] = React.useState(false);
  const [smsSent, setSmsSent] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState<string>('');
  const [turnstileLoaded, setTurnstileLoaded] = React.useState(false);
  const [turnstileVerified, setTurnstileVerified] = React.useState(false);
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const turnstileRef = React.useRef<TurnstileInstance>(null);

  const [alert, setAlert] = React.useState<AuthAlertState | null>(null);

  const isLoginDisabled = isTest ? loading : loading || !turnstileLoaded || !turnstileVerified;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlert(null);

    const email = emailInputRef.current?.value || '';
    const password = passwordInputRef.current?.value || '';

    if (!email || !password) {
      setAlert({ severity: 'error', message: 'An email and password are required' });
      return;
    }

    setLoading(true);

    sendApiRequest<
      | { require2fa?: boolean; method?: 'otplib' | 'sms'; phone?: string; theme?: string; subdomain?: string }
      | undefined
    >({
      path: '/auth/login',
      data: {
        email,
        password,
        captchaToken,
        ...(typeof router.query.accountId === 'string' ? { accountId: router.query.accountId } : {}),
      },
    })
      .then(resp => {
        if (resp && resp.require2fa) {
          setRequire2fa(true);
          setTwoFactorMethod(resp.method || 'otplib');
          setTwoFactorPhone(resp.phone || '');

          // Automatically send SMS if method is SMS
          if (resp.method === 'sms') {
            void handleSendSms();
          }
          return;
        }

        localStorage.removeItem('onboarding-robot-hidden');

        if (resp && resp.theme) {
          localStorage.setItem('theme-preference', resp.theme);
        }

        if (router.query.redirect) {
          window.location.href = router.query.redirect as string;
        } else {
          let redirectUrl = '/';

          if (resp && resp.subdomain) {
            const appUrl = getUrlApp().replace('app.', `${resp.subdomain}.`);
            redirectUrl = `${appUrl}/`;
          }

          window.location.href = redirectUrl;
        }
      })
      .catch((error: { message: string }) => {
        // Reset the Turnstile widget to allow user to solve it again
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }

        if (error.message.includes('Incorrect')) {
          setAlert({
            severity: 'error',
            message: "Incorrect email or password. If you don't have an account, please Sign Up.",
          });
          return;
        }

        setAlert({ severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (router.query.verified) {
      setAlert({ severity: 'success', message: 'Email verified successfully. Please log in to continue.' });
    }
  }, [router.query.verified]);

  const handleSendSms = async () => {
    setSendingSms(true);
    setAlert(null);

    try {
      await sendApiRequest({ path: '/auth/send-sms-code', method: 'POST' });
      setSmsSent(true);
      setAlert({ severity: 'success', message: 'SMS code sent successfully' });
    } catch (error) {
      setAlert({ severity: 'error', message: getErrorMessage(error) });
    } finally {
      setSendingSms(false);
    }
  };

  const handleVerify2fa = (event: React.FormEvent) => {
    event.preventDefault();
    setAlert(null);

    if (!code) {
      setAlert({ severity: 'error', message: 'Please enter your 6-digit 2FA code' });
      return;
    }

    setLoading2fa(true);

    sendApiRequest({ path: '/auth/verify-2fa', data: { code } })
      .then(() => {
        localStorage.removeItem('onboarding-robot-hidden');
        if (router.query.redirect) {
          window.location.href = router.query.redirect as string;
        } else {
          void router.push('/').then(() => {
            window.location.reload();
          });
        }
      })
      .catch(error => {
        setAlert({ severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => setLoading2fa(false));
  };

  return (
    <LogoutRequired>
      <Head>
        <title>Login</title>
        <meta name="description" content="Login" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <AuthContainer tab="login">
        <AuthGoogleButton label="Log in with Google" />

        <Box sx={authDividerSx}>
          <Divider
            sx={{
              '&::before, &::after': {
                borderTopWidth: 1.5,
                borderColor: 'text.secondary',
              },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
        </Box>

        {!require2fa ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }} data-testid="@login-form">
            <AuthAlert alert={alert} />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              variant="outlined"
              size="medium"
              inputRef={emailInputRef}
              slotProps={{
                htmlInput: {
                  'data-testid': '@login-email',
                },
              }}
              sx={{ mt: 0 }}
            />
            <PasswordField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              id="password"
              testId="@login-password"
              autoComplete="current-password"
              inputRef={passwordInputRef}
            />
            {!isTest && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Let us know you&apos;re a human.
                </Typography>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  scriptOptions={{
                    nonce:
                      typeof document !== 'undefined'
                        ? (document.querySelector('meta[name="csp-nonce"]') as HTMLMetaElement | null)?.content
                        : undefined,
                  }}
                  onSuccess={token => {
                    setCaptchaToken(token);
                    setTurnstileVerified(true);
                  }}
                  onWidgetLoad={() => {
                    setTurnstileLoaded(true);
                  }}
                  onError={() => {
                    setTurnstileLoaded(false);
                    setTurnstileVerified(false);
                  }}
                  data-testid="@login-human-chekcer"
                  className="turnstile-container"
                  options={{
                    size: 'flexible',
                  }}
                />
              </>
            )}

            <Button
              color="primary"
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, minHeight: 48, py: 1.25 }}
              disabled={isLoginDisabled}
              data-testid="@login-submit"
            >
              Log in
            </Button>

            <Link
              href="/forgot-password"
              sx={{
                mt: 1,
                py: 1,
                display: 'inline-block',
                width: '100%',
                textAlign: 'right',
              }}
            >
              Forgot Password?
            </Link>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerify2fa} sx={{ width: '100%' }}>
            <AuthAlert alert={alert} />
            {twoFactorMethod === 'sms' ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {smsSent ? `Enter the 6-digit code sent to ${twoFactorPhone}.` : 'Sending SMS code...'}
                </Typography>
                {smsSent && (
                  <Button
                    sx={{ mt: 2, mb: 2, py: 1.5 }}
                    variant="contained"
                    color="inherit"
                    fullWidth
                    onClick={handleSendSms}
                    disabled={sendingSms}
                  >
                    {sendingSms ? 'Sending...' : 'Resend SMS Code'}
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Enter the 6-digit code from your authenticator app.
              </Typography>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="otp"
              label="2FA Code"
              name="otp"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <Button
              color="primary"
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 2, minHeight: 48, py: 1.25 }}
              disabled={loading2fa}
            >
              Verify & Continue
            </Button>
          </Box>
        )}
      </AuthContainer>
    </LogoutRequired>
  );
}
